import {Injectable, Signal, WritableSignal, inject, linkedSignal, signal} from '@angular/core';
import {catchError, map, Observable, of, shareReplay, take} from "rxjs";
import {TYPE} from "../types/types";
import {ApiService} from "./api.service";
import {LanguageService} from './language.service';
import {UserMenuResource} from '../types/settings-types/userMenuResource';
import {CustomerMenuResource} from '../types/settings-types/customerMenuResource';
import {ModuleConfigurationService} from './module-configuration.service';
import {SettingsMenuItemType} from '../types/settings-types/settingsMenuItemType';
import {TokenService} from './token.service';
import {CapabilitiesService} from './capabilities.service';

@Injectable({
  providedIn: 'root'
})
export class NavService {
  private apiService: ApiService = inject(ApiService);
  private languageService: LanguageService = inject(LanguageService);
  private modConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private tokenService: TokenService = inject(TokenService);
  private capabilities: CapabilitiesService = inject(CapabilitiesService);

  private userMenuResponse: WritableSignal<UserMenuResource | null> = signal(null);
  private userSettingsMenuResponse: WritableSignal<UserMenuResource | null> = signal(null);

  private customerMenuResponse: WritableSignal<CustomerMenuResource | null> = signal(null);
  private customerMenuLanguage: WritableSignal<string> = signal('');

  private userMenuLoadObservable: Observable<UserMenuResource> | null = null;
  private userMenuLastLoadedTime: number = 0;

  public constructor() {
    if(this.tokenService.hasToken()) {
      this.loadUserMenu();
    }
  }

  public reloadMenus(): void {
    if(this.tokenService.hasToken()) {
      this.loadUserMenu();
      if(this.capabilities.capabilities().home.user_menu_management) {
        this.loadUserSettingsMenu();
      }
    } else {
      this.resetUserMenus();
    }
  }

  public resetUserMenus(): void {
    this.userMenuResponse.set(null);
    this.userSettingsMenuResponse.set(null);
    this.userMenuLastLoadedTime = 0;
  }

  //#region user menu
    public userMenu: Signal<TYPE.UserMenuItem[]> = linkedSignal(() => {
      const menuResponse = this.userMenuResponse();
      if(menuResponse !== null) {
        const mappedMenu: TYPE.UserMenuItem[] = [];

        menuResponse.items.forEach(item => {
          const matchingModule = this.modConfigService.menuModules.find(module => item.type === module.type);
          if(matchingModule) {
            mappedMenu.push(
              {
                ...matchingModule,
                name: item.title === null ? this.languageService.getTranslationByKey(matchingModule.name) : item.title,
              });
          }
        });
        return mappedMenu;
      } else {
        return [];
      }
    });

    private loadUserMenu(): void {
      // Skip reload if menu was loaded recently (within 2.5 seconds)
      const now = Date.now();
      const timeSinceLastLoad = now - this.userMenuLastLoadedTime;
      const RELOAD_THRESHOLD_MS = 2500;

      if (this.userMenuResponse() !== null && timeSinceLastLoad < RELOAD_THRESHOLD_MS) {
        return;
      }

      if (!this.userMenuLoadObservable) {
        this.userMenuLoadObservable = this.apiService.getUserMenu().pipe(
          shareReplay(1)
        );

        this.userMenuLoadObservable.pipe(take(1)).subscribe({
          next: (menu) => {
            this.userMenuResponse.set(menu);
            this.userMenuLastLoadedTime = Date.now();
            this.userMenuLoadObservable = null;
          },
          error: (err) => {
            console.error("Error loading user menu", err);
            this.userMenuLoadObservable = null;
          }
        });
      }
    }

    public hasUserMenuAccess(menuItemType: SettingsMenuItemType): Observable<boolean> {
      if(!this.tokenService.hasToken()) {
        return of(false);
      }

      const menuResponse = this.userMenuResponse();

      if(menuResponse !== null) {
        return of(menuResponse.items.some(item => item.type === menuItemType));
      }

      // If there's an ongoing load, wait for it instead of making a new request
      if (this.userMenuLoadObservable) {
        return this.userMenuLoadObservable.pipe(
          take(1),
          map((fetchedMenu) => {
            return fetchedMenu.items.some(item => item.type === menuItemType);
          }),
          catchError((err) => {
            console.error("Error loading user menu", err);
            return of(false);
          })
        );
      }

      return this.apiService.getUserMenu()
      .pipe(
        take(1),
        map((fetchedMenu) => {
          this.userMenuResponse.set(fetchedMenu);
          return fetchedMenu.items.some(item => item.type === menuItemType);
        }),
        catchError((err) => {
          console.error("Error loading user menu", err);
          return of(false);
        })
      );
    }

    public userSettingsMenu: Signal<TYPE.HomeIcon[]> = linkedSignal(() => {
      const menuResponse = this.userSettingsMenuResponse();
      if(menuResponse !== null) {
        const mappedMenu: TYPE.HomeIcon[] = [];

        menuResponse.items.forEach(item => {
          const matchingMenuItem = this.modConfigService.menuModules.find(module => item.type === module.type);
          if(matchingMenuItem) {
            mappedMenu.push(
              {
                ...matchingMenuItem,
                name: item.title === null ? this.languageService.getTranslationByKey(matchingMenuItem.name) : item.title,
                is_active_desktop: true, // mocked value for dialog interface
                is_active_mobile: true // mocked value for dialog interface
              });
          }
        });
        return mappedMenu;
      } else {
        return [];
      }
    });

    private loadUserSettingsMenu(): void {
      if(this.capabilities.capabilities().home.user_menu_management) {
        this.apiService.getSettingsUserMenu()
        .pipe(take(1))
        .subscribe({
          next: (menu) => {
            this.userSettingsMenuResponse.set(menu);
          },
          error: (err) => {
            console.error("Error loading user settings menu", err);
          }
        });
      }
    }
  //#endregion user menu

  //#region customer menu
  public customerMenu: Signal<TYPE.HomeIcon[]> = linkedSignal(() => {
    const menuResponse = this.customerMenuResponse();
    const menuLanguage = this.customerMenuLanguage();
    if(menuResponse !== null && menuLanguage !== '') {
      const mappedMenu: TYPE.HomeIcon[] = [];
      menuResponse.items.forEach(item => {
        const matchingMenuItem = this.modConfigService.menuModules.find(module => item.type === module.type);
        if(matchingMenuItem) {
          mappedMenu.push(
            {
              ...matchingMenuItem,
              name: item.title === null ? this.languageService.getTranslationByKey(matchingMenuItem.name) : item.title,
              is_active_desktop: item.show_on_web, 
              is_active_mobile: item.show_on_mobile,
            });
        }
      });
      return mappedMenu;
    } else {
      return [];
    }
  });

  // landing modules are additionaly filterd, if they are enabled on both the web and mobile, because the redirect is always happening after the login no matter the device
  public customerLandingModules: Signal<{id: string, name: string}[]> = linkedSignal(() => {
    const menuResponse = this.customerMenuResponse();
    const menuLanguage = this.customerMenuLanguage();
    if(menuResponse !== null && menuLanguage !== '') {
      const mappedMenu: {id: string, name: string}[] = [];
      this.modConfigService.landingModules.forEach(module => {
        const matchingMenuItem = menuResponse.items.find(item => item.type === module.type);
        // here is an extra check for module.type === null to ensure, that the dashbaoard is shown
        if(module.type === null) {
          mappedMenu.push(
            {
              id: (module.type === null ? 'dashboard' :  module.type).toLowerCase(),
              name: matchingMenuItem === undefined || matchingMenuItem?.title === null ? this.languageService.getTranslationByKey(module.name) : matchingMenuItem?.title ?? '',
            }
          );
        } else if(matchingMenuItem) {
          if(matchingMenuItem?.show_on_mobile && matchingMenuItem?.show_on_web ) {
            mappedMenu.push(
              {
                id: (module.type === null ? 'dashboard' :  module.type).toLowerCase(),
                name: matchingMenuItem === undefined || matchingMenuItem?.title === null ? this.languageService.getTranslationByKey(module.name) : matchingMenuItem?.title ?? '',
              });
          }
        }
      });
      return mappedMenu;
    } else {
      return [];
    }
  });

  // news source modules are additionaly filtered, if they are enabled on the web, because news are only shown on the web
  public customerNewsSourceModules: Signal<{id: string, name: string}[]> = linkedSignal(() => {
    const menuResponse = this.customerMenuResponse();
    const menuLanguage = this.customerMenuLanguage();
    if(menuResponse !== null && menuLanguage !== '') {
      const mappedMenu: {id: string, name: string}[] = [];
      this.modConfigService.newsSourceModules.forEach(module => {
        const matchingMenuItem = menuResponse.items.find(item => item.type === module.type);
        if(matchingMenuItem || module.type === null) {
          if(matchingMenuItem?.show_on_web ) {
            mappedMenu.push(
              {
                id: (module.type === null ? 'dashboard' :  module.type).toLowerCase(),
                name: matchingMenuItem === undefined || matchingMenuItem?.title === null ? this.languageService.getTranslationByKey(module.name) : matchingMenuItem?.title ?? '',
              });
          }
        }
      });
      return mappedMenu;
    } else {
      return [];
    }
  });

  public loadCustomerMenu(lang: string): void {
    if(lang) {
      this.customerMenuLanguage.set(lang);
      this.customerMenuResponse.set(null); // reset menu while loading to prevent showing wrong menu for a short time
      this.apiService.getSettingsCustomerMenu(lang)
      .pipe(take(1))
      .subscribe({
        next: (menu) => {
          this.customerMenuResponse.set(menu);
        },
        error: (err) => {
          console.error("Error loading customer menu", err);
        }
      });
    } else {
      this.resetCustomerMenu();
    }
  }

  public resetCustomerMenu(): void {
    this.customerMenuResponse.set(null);
    this.customerMenuLanguage.set('');
  }
  //#endregion customer menu
}
